/* @flow */
import * as React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import querystring from "querystring";
import styled, { css } from "styled-components";

import FeatureFlags from "../../../FeatureFlags";
import Gettext, { mapStateToGettext } from "../../common/Gettext";
import { mapDispatchToFetch } from "../../common/fetch";
import { withDeposit } from "../../common/withDeposit";
import { isWebApp } from "../../common/Link";
import {
  readSignInParameters,
  flushSignInParameters,
  getSub0,
} from "../../Subzero";
import { LOGIN_TAB_ID, REGISTER_TAB_ID } from "../Auth";
import Tab from "../Tab";
import SocialLink from "../SocialLink";
import BtnFormSubmit from "../BtnFormSubmit";
import Message from "../ErrorMessage";
import CountrySelect from "../CountrySelect";
import validators from "../validators";
import loginWith from "../loginWith";
import Container from "../StyledComponents/Container";
import Input from "../Input";
import PhoneInput, {
  PhoneInputHint,
  ripOffLeadingZero,
} from "../../ReVerification/Phone/PhoneInput";
import { FieldContainer } from "../../ReVerification/style";
import { InfoIcon } from "../../ReVerification/Icons";
import { fontSize, rtl } from "../../common/styled-mixins";
import Link from "../StyledComponents/FakeLink";
import { gaReducer } from "../../GA";
import { Link as RouterLink } from "../../common/Link";
import { submitAppsFlyerParameters } from "../../Scorpion";

const isIronForm =
  process.env.REACT_APP_DEPLOY_TARGET === "irontrade" ||
  process.env.REACT_APP_DEPLOY_TARGET === "eztrader";

const Unbreakable = styled.span`
  white-space: nowrap;
`;

const TabContainer = styled.div`
  width: 100%;
  margin-bottom: auto;
  font-size: 0;
  line-height: 0;
  vertical-align: bottom;
  border-bottom: 2px solid ${({ theme }) => theme.accent};
`;

const RegisterContainer = styled(Container)`
  padding-top: 4px;
`;

const InputContainer = styled.div`
  padding: 4px 0;
  position: relative;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      padding: 9px 0;
    `};
`;

const CheckContainer = styled.div`
  padding: 8px 0;
`;

const Checkbox = styled.input`
  display: none;
`;

const CheckboxLabel = styled.label`
  direction: ${(props) => props.theme.direction};
  position: relative;
  display: block;
  ${rtl("padding-left", "31px")};
  font-size: ${fontSize(12, 16)};
  line-height: 1.2;
  ${rtl("text-align", "left")};
  color: #8d8d8d !important;
  user-select: none;

  :before {
    content: "";
    position: absolute;
    top: 50%;
    ${rtl("left", 0)};
    transform: translateY(-50%);
    width: 19px;
    height: 19px;
    border: 1px solid #cfcfcf;
    border-radius: 3px;
    color: #252525;
    background: none;
    background-color: #f6f6f6;
    box-shadow: inset 0 2px 0 0 #e3e3e3;
    cursor: pointer;
  }

  :after {
    ${({ checked }) =>
      checked &&
      css`
        content: "";
        position: absolute;
        top: 50%;
        ${rtl("left", "5px")};
        transform: rotate(45deg);
        width: 8px;
        height: 12px;
        margin-top: -8px;
        border-right: 3px solid #56496e;
        border-bottom: 3px solid #56496e;
        cursor: pointer;
      `};
  }

  ${({ theme }) =>
    theme.newIndex &&
    css`
      color: #ffffff !important;
      ${rtl("padding-left", "47px")};

      &::before {
        width: 30px;
        height: 30px;
        background-color: rgba(255, 255, 255, 0.25);
        border: none;
        border-radius: 8px;
        box-shadow: none;
      }

      &:after {
        ${({ checked, isDarkPage }) =>
          checked &&
          css`
            ${rtl("left", "11px")};
            border-right-color: ${isDarkPage ? "#ffffff" : "#56496e"};
            border-bottom-color: ${isDarkPage ? "#ffffff" : "#56496e"};
          `};
      }
    `};
`;

const TermsLink = styled(RouterLink)`
  font-size: ${fontSize(12, 16)};
  line-height: 1.2 !important;
  text-decoration: none !important;
  color: #feac00 !important;
  transition: all 0.2s ease !important;

  :hover,
  :active,
  :focus {
    color: #ffc54c !important;
  }

  ${({ theme }) =>
    theme.newIndex &&
    css`
      color: ${theme.accent} !important;

      &:hover {
        color: ${theme.mainLink} !important;
      }
    `};
`;

const SubmitContainer = styled.div`
  direction: ${(props) => props.theme.direction};
  padding: 4px 0;
  height: 56px;
  position: relative;
  text-align: justify;
  vertical-align: middle;

  ${(props) =>
    !props.showSocial &&
    css`
      text-align: center !important;
    `};

  :after {
    content: "";
    display: inline-block;
    width: 100%;
  }

  ${(props) =>
    props.isIntlProject &&
    css`
      display: flex;
      justify-content: space-between;
      align-items: center;

      ::after {
        display: none;
      }

      @media (max-width: 374px) {
        height: auto;
        text-align: center;
      }
    `};
`;

const Text = styled.span`
  ${rtl("margin-left", "4px")};
  display: inline-block;
  vertical-align: middle;
  font-size: ${fontSize(12)};
  line-height: 1.2;
  text-transform: uppercase;
  color: #fff;

  ${(props) =>
    props.isIntlProject &&
    css`
      text-transform: lowercase;
      margin: 0 5px !important;
    `};
`;

const PhoneContainer = styled(InputContainer)`
  direction: ${({ theme }) => theme.direction};
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  margin: 4px 0;

  ${({ theme }) =>
    theme.newIndex &&
    css`
      margin: 9px 0;
    `};
`;

const PhoneInputContainer = styled.div`
  @media (max-width: 767px) {
    :first-of-type {
      margin-bottom: 4px;
    }
  }

  @media (min-width: 768px) {
    flex: 1;
    flex-basis: 50%;
    max-width: 220px;
  }
`;

const PhoneFieldContainer = styled(FieldContainer)`
  position: relative;
  width: 100%;

  @media (max-width: 959px) {
    display: flex;
  }
`;

const PhoneInfo = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  width: 100%;
  margin-bottom: 8px;
  padding: 27px 20px 20px 30px;
  background: #ebebeb;
  border-radius: 4px;

  :after {
    top: 100%;
    left: 18px;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: rgba(235, 235, 235, 0);
    border-top-color: #ebebeb;
    border-width: 8px;
    margin-left: -8px;
  }
`;

const IconContainer = styled.div`
  position: absolute;
  width: 16px;
  min-width: 16px;
  margin-right: 7px;
  top: 10px;
  left: 9px;
`;

const InfoText = styled.div`
  font-size: ${fontSize(12)};
  line-height: 14px;
  color: #343434;
  text-align: left;
`;

const CloseInfo = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 11px;
  height: 11px;
  cursor: pointer;
`;

const FormText = styled.p`
  padding: 0 20px 9px 20px;
  margin: 0;
  font-size: ${fontSize(18)};
  line-height: 24px;
  font-weight: bold;
  color: ${({ theme }) => theme.mainDarkText};
  text-align: center;
  ${({ theme }) =>
    theme.newIndex &&
    css`
      color: #ffffff;
      font-size: 28px;
      line-height: 1.2;

      @media (max-width: 479px) {
        font-size: 20px;
      }
    `};
`;

const BackContainer = styled.div`
  margin-top: 10px;
  font-size: ${fontSize(15)};
  color: #fff;
  line-height: 1;
  ${({ theme, isDarkPage }) =>
    theme.newIndex &&
    css`
      font-size: ${fontSize(18)};
      text-align: center;
      line-height: 1.2;
      font-weight: bold;
      color: ${isDarkPage ? "#ffffff" : "rgba(34, 34, 34, 0.59)"};
    `};
`;

const BackToLogin = styled(Link)`
  text-decoration: none !important;
  color: ${({ theme }) => theme.accent} !important;
  transition: all 0.2s ease;
  font-size: ${fontSize(18)};
  line-height: 1;

  :hover,
  :active,
  :focus {
    color: #ffc54c !important;
  }
  cursor: pointer;
  ${({ theme }) =>
    theme.newIndex &&
    css`
      color: ${theme.accent} !important;
      &:hover {
        color: #ffffff !important;
      }
    `};
`;

export function getPhoneExample(telCountry, apiCountries) {
  let phoneExample;
  if (telCountry) {
    const apiCountry = apiCountries.find(
      (country) => country.code.toUpperCase() === telCountry.code.toUpperCase()
    );
    phoneExample = apiCountry && apiCountry["phone-number"];

    const code = `+${telCountry.phoneCode}`;
    if (phoneExample && phoneExample.indexOf(code) > -1) {
      phoneExample = phoneExample.slice(code.length).trim();
    }
  }

  return phoneExample;
}

type Country = *;

type Props = {
  email: string,
  tel: string,
  password: string,
  agree: boolean,
  ref_user: string,
  onInputChange: EventHandler,
  onLeave: (string) => void,
  countryList: Array<Country>,
  country: Country,
  showSocial: boolean,
  locale: string,
};

type State = {
  pending: "host" | "fb" | "gp" | null,
  submitTimeError: string | React.Node | null,
  responseTimeError: string | null,
  showPhoneInfo: boolean,
};

function isAndroidAppMessagingAvailable() {
  return (
    window.nativeInterface != null &&
    window.nativeInterface.onFacebookLoginClick != null
  );
}

function isIOSAppMessagingAvailable() {
  return (
    window.webkit != null &&
    window.webkit.messageHandlers != null &&
    window.webkit.messageHandlers["nativeInterface"] != null
  );
}

const useNativeInterface = (fnName = "", data) => {
  try {
    if (
      window.nativeInterface != null &&
      typeof window.nativeInterface[fnName] === "function"
    ) {
      window.nativeInterface[fnName](data);
    }
    if (isIOSAppMessagingAvailable()) {
      window.webkit.messageHandlers["nativeInterface"].postMessage(
        JSON.stringify({
          function: fnName,
          param: data,
        })
      );
    }
  } catch (error) {
    console.log(error);
  }
};

class RegisterMain extends React.Component<Props, State> {
  state = {
    shouldShowPhoneHint: true,
    pending: null,
    submitTimeError: null,
    responseTimeError: null,
    showPhoneInfo: false,
    agreeError: null,
    emailError: null,
    countryError: null,
    telError: null,
    passwordError: null,
  };

  register = async (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const {
      email,
      tel: phone,
      password,
      agree,
      agreeDailyMail,
      country,
      ref_user,
      location,
      locale,
    } = this.props;

    const phoneCode = this.state.telCountry && this.state.telCountry.phoneCode;
    let tel = phone;

    if (phoneCode) {
      tel = ripOffLeadingZero(phoneCode, phone);
    }

    gaReducer("CLICKBTN_REGISTER_MAINPAGE", { userId: "N/A" });

    const telValidation = validators.tel(tel);
    const submitTimeError =
      validators.agree(agree) ||
      validators.emptyFields(email, country, password, tel) ||
      validators.emailFull(email) ||
      validators.country(country) ||
      telValidation ||
      validators.password(password);

    let errors = {};
    errors.agreeError = validators.agree(agree);
    errors.emailError = validators.emailFull(email);
    errors.countryError = validators.country(country);
    errors.passwordError = validators.password(password);
    errors.telError = telValidation;

    if (submitTimeError) {
      if (submitTimeError === telValidation) {
        let phoneExample = "";
        if (this.state.telCountry) {
          const apiCountry = this.props.apiCountries.find(
            (country) =>
              country.code.toUpperCase() ===
              this.state.telCountry.code.toUpperCase()
          );
          phoneExample = apiCountry && apiCountry["phone-number"];
        }

        if (typeof this.props.logPhoneErrors === "function") {
          this.props.logPhoneErrors({
            phone: tel,
            error_msg: "auth: Invalid phone number",
            source: "registration",
            phone_example: phoneExample || "",
          });
        }

        /* prettier-ignore */
        this.setState({
          telError: phoneExample ? (
            <Gettext>
              Incorrect phone format! Example: <Unbreakable>{phoneExample}</Unbreakable>
            </Gettext>
          ) : (
            this.props.gettext`Incorrect phone number format!`
          )
        });
      } else {
        this.setState(errors);
      }

      return;
    }

    this.setState({ pending: "host" });

    const origin = window.location.hostname + location.pathname;

    const statistics = readSignInParameters();

    const credentials = querystring.stringify({
      ...statistics,
      sub0: getSub0(window.location, this.props.serverData),
      email,
      password,
      password2: password,
      currency: country.currency,
      agree: agree ? "on" : "off",
      daily_mail: agreeDailyMail ? 1 : 0,
      phone: tel,
      locale,
      ...(location.pathname === "/regbonus" ? { origin } : null),
      ...(ref_user ? { ref_user } : null),
      site_url:window.location.href,
      // reg_scenario: "reg_phone_confirm"
    });

    try {
      const response = await this.props.fetch(`/register?${credentials}`, {
        unstable_dontInferResponse: true,
      });

      this.setState({ pending: null });

      switch (response.status) {
        case "OK":
          this.props.dispatch({ type: "REQUEST_PROFILE" });
          flushSignInParameters();
          submitAppsFlyerParameters();

          if (agreeDailyMail) {
            this.props.fetch("/profile-update?daily_mail=1", {
              unstable_dontInferResponse: true,
            });
          }

          // if (this.props.match.url === "/regbonus") {
          //   this.props.history.push("/");
          // }

          if (!this.props.noRedirect) {
            const query = querystring.parse(
              window.location.search.substring(1)
            );
            if (!query.redirect || query.redirect === "false") {
              this.props.history.push("/trade");
            }
          }
          useNativeInterface("setUserKey", response.user_key);
          break;
        case "ERROR":
          this.setState({ responseTimeError: response.message });
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }

    try {
      window.oSpP && window.oSpP.push("Email", email);
    } catch (error) {
      console.log("Error from sendpulse push service", error);
    }
  };

  handleInputChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({
      [`${event.target.name}Error`]: null,
      submitTimeError: null,
      responseTimeError: null,
    });

    this.props.onInputChange(event);
  };

  handlePhoneInputChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({
      telError: null,
      submitTimeError: null,
      responseTimeError: null,
    });

    this.props.onPhoneInputChange(event);
  };

  socialAuthGate = (network: string, event: SyntheticMouseEvent<*>) => {
    //для веб-вью приложения авторизация через FB SDK, которую делает Малышев и редиректит с токеном фб на бэк, где получает наш токен и редиректит на платформу с этим токеном, где авторизуется.
    if (network === "fb") {
      if (isAndroidAppMessagingAvailable()) {
        event.preventDefault();
        window.nativeInterface.onFacebookLoginClick();
      } else if (isIOSAppMessagingAvailable()) {
        event.preventDefault();
        window.webkit.messageHandlers["nativeInterface"].postMessage(
          JSON.stringify({
            function: "onFacebookLoginClick",
          })
        );
      }
    }
    this.setState({ pending: network });
  };

  showInfo = () => this.setState({ showPhoneInfo: true });

  hideInfo = () => this.setState({ showPhoneInfo: false });

  getPhoneExample = () =>
    getPhoneExample(this.state.telCountry, this.props.apiCountries);

  render() {
    const {
      agree,
      agreeDailyMail,
      tel,
      email,
      password,
      country,
      countryList,
      showSocial,
      showDeposit,
      isDarkPage,
    } = this.props;

    const {
      pending,
      agreeError,
      countryError,
      emailError,
      telError,
      passwordError,
      submitTimeError,
      responseTimeError,
    } = this.state;

    const inputTimeError = validators.email(email);
    const errorMessage = responseTimeError || submitTimeError || inputTimeError;

    const urlParams = querystring.parse(window.location.search.substring(1));

    const tabs = (
      <TabContainer>
        <Tab id={REGISTER_TAB_ID} isActive={true}>
          <Gettext>Registration</Gettext>
        </Tab>
        <Tab
          id={LOGIN_TAB_ID}
          isActive={false}
          onClick={() => this.props.onLeave(LOGIN_TAB_ID)}
        >
          <Gettext>Log in</Gettext>
        </Tab>
      </TabContainer>
    );

    const phoneExample = this.getPhoneExample();

    return (
      <React.Fragment>
        {!isIronForm && tabs}
        <RegisterContainer>
          <form method="post" id="registerForm">
            {isIronForm && (
              <div>
                <FormText>
                  <Gettext>Create your free account</Gettext>
                </FormText>
              </div>
            )}
            {urlParams.subid && (
              <input
                type="hidden"
                name="subid"
                defaultValue={encodeURIComponent(urlParams.subid)}
              />
            )}
            {urlParams.site_domain && (
              <input
                type="hidden"
                name="site_domain"
                defaultValue={encodeURIComponent(urlParams.site_domain)}
              />
            )}
            {urlParams.clickid && (
              <input
                type="hidden"
                name="clickid"
                defaultValue={encodeURIComponent(urlParams.clickid)}
              />
            )}{" "}
            <Input
              tab="register"
              type="email"
              name="email"
              id="register-email"
              required="required"
              placeholder={this.props.gettext`Email`}
              value={email}
              onChange={this.handleInputChange}
              autocomplete="email"
            />
            {emailError && <Message message={emailError} />}
            <input
              className="sp_push_custom_data"
              type="hidden"
              name="Email"
              value={email}
            />
            {isIronForm && (
              <InputContainer>
                <CountrySelect
                  name="country"
                  value={country}
                  options={countryList}
                  onChange={this.handleInputChange}
                />
              </InputContainer>
            )}
            {countryError && <Message message={countryError} />}
            {FeatureFlags.useCoolPhoneInputOnAuth ? (
              <PhoneFieldContainer>
                <PhoneContainer>
                  {this.state.showPhoneInfo && this.state.shouldShowPhoneHint && (
                    <PhoneInfo>
                      <IconContainer>
                        <InfoIcon />
                      </IconContainer>
                      <InfoText>
                        <PhoneInputHint
                          countryCode={
                            this.state.telCountry && this.state.telCountry.code
                          }
                          phoneExample={phoneExample}
                        />
                      </InfoText>
                      <CloseInfo onClick={this.hideInfo}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="4454.792 3403.601 10.606 10.607"
                        >
                          <g transform="translate(3350 3251)">
                            <rect
                              width="1"
                              height="14"
                              fill="#b5b5b5"
                              transform="translate(1114.691 152.601) rotate(45)"
                            />
                            <rect
                              width="1"
                              height="14"
                              fill="#b5b5b5"
                              transform="translate(1115.398 162.5) rotate(135)"
                            />
                          </g>
                        </svg>
                      </CloseInfo>
                    </PhoneInfo>
                  )}
                  <PhoneInput
                    SelectContainer={PhoneInputContainer}
                    InputContainer={PhoneInputContainer}
                    defaultCountry={this.props.geo}
                    value={tel}
                    onChange={({
                      country,
                      fullNumber: phone,
                      countryAutoSwitched,
                    }) => {
                      this.handleInputChange({
                        target: {
                          name: "tel",
                          value: phone,
                        },
                      });

                      const countryCode =
                        this.state.telCountry && this.state.telCountry.code;
                      let shouldShowPhoneHint = countryAutoSwitched
                        ? false
                        : this.state.shouldShowPhoneHint;
                      if (
                        !countryAutoSwitched &&
                        country.code !== countryCode
                      ) {
                        shouldShowPhoneHint = true;
                      }
                      this.setState({
                        shouldShowPhoneHint,
                        telCountry: country,
                      });
                    }}
                    // errorMessage={errorMessage}
                    placeholder={this.props.gettext`Phone`}
                    showInfo={<PhoneInputHint />}
                    onFocus={this.showInfo}
                    onBlur={this.hideInfo}
                  />
                </PhoneContainer>
              </PhoneFieldContainer>
            ) : (
              <Input
                tab="register"
                type="tel"
                name="tel"
                id="register-phone"
                required="required"
                placeholder={this.props.gettext`Phone`}
                value={tel}
                onChange={this.handlePhoneInputChange}
                autocomplete="tel"
              />
            )}
            {telError && <Message message={telError} />}
            <Input
              tab="register"
              type="password"
              name="password"
              id="register-pass"
              required="required"
              placeholder={this.props.gettext`Password`}
              value={password}
              onChange={this.handleInputChange}
            />
            {passwordError && <Message message={passwordError} />}
            <CheckContainer>
              <Checkbox
                id="agreeChk"
                type="checkbox"
                checked={agree}
                name="agree"
                onChange={this.handleInputChange}
              />
              <CheckboxLabel
                checked={agree}
                htmlFor="agreeChk"
                isDarkPage={isDarkPage}
              >
                {process.env.REACT_APP_DEPLOY_TARGET === "eztrader" ? (
                  <Gettext>I'm over 18 years old</Gettext>
                ) : (
                  <React.Fragment>
                    <Gettext>I'm over 18 years old and I agree with</Gettext>{" "}
                    <TermsLink
                      to="/conditions"
                      target="_blank"
                      title={this.props.gettext`Terms and Conditions`}
                      onClick={isWebApp() ? this.props.onDismissPopup : null}
                    >
                      <Gettext>Terms and Conditions</Gettext>
                    </TermsLink>{" "}
                    {showDeposit && [
                      <Gettext key={0}>and</Gettext>,
                      " ",
                      <TermsLink
                        key={1}
                        to="/return-refund-policy"
                        target="_blank"
                        title={this.props.gettext`Return and Refund Policy`}
                        onClick={isWebApp() ? this.props.onDismissPopup : null}
                      >
                        <Gettext>Return and Refund Policy</Gettext>
                      </TermsLink>,
                      " ",
                      <Gettext key={2}>and</Gettext>,
                      " ",
                      <TermsLink
                        key={3}
                        to="/privacy-policy"
                        target="_blank"
                        title={this.props.gettext`Privacy Policy`}
                        onClick={isWebApp() ? this.props.onDismissPopup : null}
                      >
                        <Gettext>Privacy Policy</Gettext>
                      </TermsLink>,
                    ]}
                  </React.Fragment>
                )}
              </CheckboxLabel>
            </CheckContainer>
            {agreeError && <Message message={agreeError} />}
            <CheckContainer style={{ margin: "8px 0" }}>
              <Checkbox
                id="agreeDailyChk"
                type="checkbox"
                checked={agreeDailyMail}
                name="agreeDailyMail"
                onChange={this.handleInputChange}
              />
              <CheckboxLabel
                checked={agreeDailyMail}
                htmlFor="agreeDailyChk"
                isDarkPage={isDarkPage}
              >
                <Gettext>
                  Subscribe me to special offers and tutorial content
                </Gettext>
              </CheckboxLabel>
            </CheckContainer>
            {errorMessage && <Message message={errorMessage} />}
            <SubmitContainer showSocial={showSocial} isIntlProject={isIronForm}>
              <BtnFormSubmit
                id="register"
                onClick={this.register}
                loader={pending === "host"}
              >
                <Gettext>Register</Gettext>
              </BtnFormSubmit>{" "}
              {(showSocial || isIronForm) && (
                <React.Fragment>
                  <Text isIntlProject={isIronForm}>
                    <Gettext>or register with</Gettext>
                  </Text>{" "}
                  <SocialLink
                    network="fb"
                    pending={pending === "fb"}
                    href={loginWith("fb", this.props)}
                    onClick={(event) => this.socialAuthGate("fb", event)}
                  />
                  <SocialLink
                    network="gp"
                    pending={pending === "gp"}
                    href={loginWith("gp", this.props)}
                    onClick={(event) => this.socialAuthGate("gp", event)}
                  />
                </React.Fragment>
              )}
            </SubmitContainer>
            {isIronForm && (
              <BackContainer isDarkPage={isDarkPage}>
                <span>
                  <Gettext>Already have an account? Then</Gettext>{" "}
                </span>
                <BackToLogin onClick={() => this.props.onLeave(LOGIN_TAB_ID)}>
                  <Gettext>login</Gettext>
                </BackToLogin>
              </BackContainer>
            )}
          </form>
        </RegisterContainer>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    ...mapStateToGettext(state),
    serverData: state.serverData,
  };
}

export default withRouter(
  withDeposit(
    connect(
      mapStateToProps,
      mapDispatchToFetch
    )(RegisterMain)
  )
);



// WEBPACK FOOTER //
// ./src/components/Auth/Tabs/RegisterMain.js